/**
 * Linked OAuth Accounts Settings
 * Design System v2 - Dashboard style (light glassmorphism)
 * Manage Google & Microsoft linked accounts
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { oauthAPI, OAuthLinkedAccount, OAuthProvider } from '@/utils/api';
import { logger } from '@/utils/logger';
import { notifications } from '@/lib/notifications';
import { confirm } from '@/stores/confirmStore';
import {
  Link2,
  Unlink,
  Shield,
  Loader2,
  CheckCircle,
  AlertCircle,
  ExternalLink
} from 'lucide-react';

// Google Icon SVG
const GoogleIcon = ({ className = '' }: { className?: string }) => (
  <svg viewBox="0 0 24 24" className={className} xmlns="http://www.w3.org/2000/svg">
    <g transform="matrix(1, 0, 0, 1, 0, 0)">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </g>
  </svg>
);

// Microsoft Icon SVG
const MicrosoftIcon = ({ className = '' }: { className?: string }) => (
  <svg viewBox="0 0 23 23" className={className} xmlns="http://www.w3.org/2000/svg">
    <rect fill="#f25022" x="1" y="1" width="10" height="10"/>
    <rect fill="#00a4ef" x="1" y="12" width="10" height="10"/>
    <rect fill="#7fba00" x="12" y="1" width="10" height="10"/>
    <rect fill="#ffb900" x="12" y="12" width="10" height="10"/>
  </svg>
);

interface ProviderCardProps {
  provider: 'GOOGLE' | 'MICROSOFT';
  linkedAccount?: OAuthLinkedAccount;
  isEnabled: boolean;
  onLink: () => void;
  onUnlink: () => void;
  loading: boolean;
}

const ProviderCard: React.FC<ProviderCardProps> = ({
  provider,
  linkedAccount,
  isEnabled,
  onLink,
  onUnlink,
  loading,
}) => {
  const { t } = useTranslation();
  const config = {
    GOOGLE: {
      name: 'Google',
      icon: GoogleIcon,
      description: t('auth.linkedAccounts.googleDescription'),
    },
    MICROSOFT: {
      name: 'Microsoft',
      icon: MicrosoftIcon,
      description: t('auth.linkedAccounts.microsoftDescription'),
    },
  };

  const { name, icon: Icon, description } = config[provider];
  const isLinked = !!linkedAccount;

  return (
    <div className="bg-white/60 backdrop-blur-sm border border-gray-200 rounded-2xl p-5 hover:bg-white/80 transition-all duration-300">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center">
            <Icon className="w-6 h-6" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">{name}</h3>
            {isLinked ? (
              <div className="flex items-center gap-2 text-sm text-green-600">
                <CheckCircle className="w-4 h-4" />
                <span>{linkedAccount.email}</span>
              </div>
            ) : (
              <p className="text-sm text-gray-500">{description}</p>
            )}
          </div>
        </div>

        <div>
          {!isEnabled ? (
            <span className="text-sm text-gray-400">{t('auth.linkedAccounts.notAvailable')}</span>
          ) : isLinked ? (
            <button
              onClick={onUnlink}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-xl transition-all duration-200 disabled:opacity-50"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Unlink className="w-4 h-4" />
              )}
              {t('auth.linkedAccounts.unlink')}
            </button>
          ) : (
            <button
              onClick={onLink}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-teal-600 hover:text-teal-700 hover:bg-teal-50 rounded-xl transition-all duration-200 disabled:opacity-50"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Link2 className="w-4 h-4" />
              )}
              {t('auth.linkedAccounts.link')}
            </button>
          )}
        </div>
      </div>

      {isLinked && linkedAccount && (
        <div className="mt-3 pt-3 border-t border-gray-100 text-xs text-gray-500">
          {t('auth.linkedAccounts.linkedAt', { date: new Date(linkedAccount.linkedAt).toLocaleDateString('fr-FR', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
          }) })}
        </div>
      )}
    </div>
  );
};

export const LinkedAccountsSettings: React.FC = () => {
  const { t } = useTranslation();
  const [linkedAccounts, setLinkedAccounts] = useState<OAuthLinkedAccount[]>([]);
  const [providers, setProviders] = useState<OAuthProvider[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [accountsData, providersData] = await Promise.all([
        oauthAPI.getLinkedAccounts(),
        oauthAPI.getProviders(),
      ]);
      setLinkedAccounts(accountsData);
      setProviders(providersData);
      setError(null);
    } catch (err: any) {
      logger.error('Failed to fetch OAuth data:', err);
      setError(t('auth.linkedAccounts.loadError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleLink = (provider: 'google' | 'microsoft') => {
    setActionLoading(provider);
    oauthAPI.initiateLink(provider);
  };

  const handleUnlink = async (provider: 'google' | 'microsoft') => {
    const confirmed = await confirm({
      title: t('auth.linkedAccounts.confirmUnlinkTitle'),
      message: t('auth.linkedAccounts.confirmUnlinkMessage', { provider: provider === 'google' ? 'Google' : 'Microsoft' }),
      variant: 'warning',
      confirmText: t('auth.linkedAccounts.confirmUnlink'),
      cancelText: t('auth.changePassword.cancel'),
    });
    if (!confirmed) {
      return;
    }

    setActionLoading(provider);
    try {
      await oauthAPI.unlinkAccount(provider);
      notifications.success(t('auth.linkedAccounts.unlinkSuccess', { provider: provider === 'google' ? 'Google' : 'Microsoft' }));
      await fetchData();
    } catch (err: any) {
      logger.error('Failed to unlink account:', err);
      notifications.error(err.message || t('auth.linkedAccounts.unlinkError'));
    } finally {
      setActionLoading(null);
    }
  };

  const getLinkedAccount = (provider: 'GOOGLE' | 'MICROSOFT') => {
    return linkedAccounts.find((a) => a.provider === provider);
  };

  const isProviderEnabled = (provider: 'GOOGLE' | 'MICROSOFT') => {
    return providers.some((p) => p.provider === provider && p.enabled);
  };

  if (loading) {
    return (
      <div className="bg-white/20 backdrop-blur-xl rounded-3xl border border-white/30 shadow-xl shadow-gray-300/10 p-8 mb-6">
        <div className="flex items-center gap-4 mb-8">
          <div className="p-4 bg-gradient-to-br from-teal-50 to-teal-100/50 rounded-2xl shadow-sm">
            <Shield className="w-6 h-6 text-teal-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">{t('auth.linkedAccounts.title')}</h2>
            <p className="text-sm text-gray-500">{t('auth.linkedAccounts.subtitle')}</p>
          </div>
        </div>
        <div className="space-y-4">
          <div className="h-24 bg-white/30 rounded-2xl animate-pulse" />
          <div className="h-24 bg-white/30 rounded-2xl animate-pulse" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white/20 backdrop-blur-xl rounded-3xl border border-white/30 shadow-xl shadow-gray-300/10 p-8 mb-6">
        <div className="flex items-center gap-4 mb-6">
          <div className="p-4 bg-gradient-to-br from-red-50 to-red-100/50 rounded-2xl shadow-sm">
            <AlertCircle className="w-6 h-6 text-red-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">{t('auth.linkedAccounts.title')}</h2>
            <p className="text-sm text-red-600">{error}</p>
          </div>
        </div>
        <button
          onClick={fetchData}
          className="flex items-center gap-2 px-6 py-3 bg-teal-600 hover:bg-teal-700 text-white rounded-2xl font-semibold transition-all duration-300 shadow-lg shadow-teal-500/30 hover:shadow-xl"
        >
          {t('auth.linkedAccounts.retry')}
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white/20 backdrop-blur-xl rounded-3xl border border-white/30 shadow-xl shadow-gray-300/10 p-8 mb-6">
      <div className="flex items-center gap-4 mb-8">
        <div className="p-4 bg-gradient-to-br from-teal-50 to-teal-100/50 rounded-2xl shadow-sm">
          <Shield className="w-6 h-6 text-teal-600" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-gray-900">{t('auth.linkedAccounts.title')}</h2>
          <p className="text-sm text-gray-500">
            {t('auth.linkedAccounts.description')}
          </p>
        </div>
      </div>

      <div className="space-y-4">
        <ProviderCard
          provider="GOOGLE"
          linkedAccount={getLinkedAccount('GOOGLE')}
          isEnabled={isProviderEnabled('GOOGLE')}
          onLink={() => handleLink('google')}
          onUnlink={() => handleUnlink('google')}
          loading={actionLoading === 'google'}
        />

        <ProviderCard
          provider="MICROSOFT"
          linkedAccount={getLinkedAccount('MICROSOFT')}
          isEnabled={isProviderEnabled('MICROSOFT')}
          onLink={() => handleLink('microsoft')}
          onUnlink={() => handleUnlink('microsoft')}
          loading={actionLoading === 'microsoft'}
        />
      </div>

      {/* Info note */}
      <div className="mt-8 p-5 bg-white/40 backdrop-blur-sm border border-teal-100 rounded-2xl">
        <div className="flex items-start gap-3">
          <ExternalLink className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-gray-700">
            <p className="font-semibold text-gray-900">{t('auth.linkedAccounts.infoTitle')}</p>
            <p className="text-gray-600 mt-1">
              {t('auth.linkedAccounts.infoText')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LinkedAccountsSettings;
